import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  Grid,
  Alert,
  Snackbar,
  AlertColor,
  createTheme,
  CircularProgress,
  Typography,
} from "@mui/material";
import PageTitle from "../../molecules/PageTitle";
import { BuildingType } from "./BuildingSelectionModal";
import { useNavigate, useParams } from "react-router-dom";
import { getJwtTokenFromIdToken } from "../auth/AuthFanc";
import { CustomContainer } from "../../../styles/styles";
import ConfirmDialog from "../../molecules/ConfirmDialog";
import {
  IpV4ErrorMessage,
  IpV4Regex,
  StaticIpErrorMessage,
  StaticIpRegex,
} from "../../../utils/ValidationHelper";
import {
  blankDoorDetailsValidationErrors,
  defaultSdc,
  DoorDetailsTable,
  DoorDetailsValidationErrors,
  SdcDetails,
} from "./DoorDetailsTable";
import {
  ErrorWithRetry,
  LoadingSpinner,
} from "../../molecules/LoadingAndError";

const DoorEditPage: React.FC = () => {
  const theme = createTheme({
    spacing: [1, 4, 8, 12, 20, 32, 52, 84, 136, 220, 356],
  });

  const { autoSdcIdInUrl } = useParams();
  const navigate = useNavigate();
  const [sdc, setSdc] = useState<SdcDetails>(defaultSdc);
  const [fetchedTs, setFetchedTs] = useState<number>(0);
  // formEdited は最初は false であり、それを変更する関数が setFormEdited
  const [formEdited, setFormEdited] = useState(false);
  const [initialBillingStartAt, setInitialBillingStartAt] = useState<
    string | null
  >(null);

  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);

  const [isUpdating, setIsUpdating] = useState(false);

  // アラート
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [alertSeverity, setAlertSeverity] = useState<AlertColor>("success");

  const [openConfirmDialog, setOpenConfirmDialog] = useState(false);

  const handleCloseSnackbar = () => {
    setSnackbarOpen(false);
    // window.location.reload();//下部アラートが消えた後に画面リロード
  };

  // 確認ダイアログ
  const [openConfirm, setOpenConfirm] = React.useState(false);
  const [openConfirmRestart, setOpenConfirmRestart] = React.useState(false);
  const [isRestartOngoing, setIsRestartOngoing] = React.useState(false);

  const CHARACTER_LIMIT = 50; // 文字数制限を定義

  const [isTouched, setIsTouched] = useState({
    doorPlace: false,
    memo: false,
    ap1IpConfigType: false,
    ap2IpConfigType: false,
    ap3IpConfigType: false,
    isLteEnabled: false,
    isWifiEnabled: false,
    ap1StaticIp: false,
    ap2StaticIp: false,
    ap3StaticIp: false,
    sciSignageIpAddr: false,
  });

  const [validationErrors, setValidationErrors] =
    useState<DoorDetailsValidationErrors>(blankDoorDetailsValidationErrors);

  // 入力が変更されたときのハンドラ
  const handleBlur = (field: string) => {
    setIsTouched({
      ...isTouched,
      [field]: true,
    });
  };

  useEffect(() => {
    const errors = {
      doorPlace:
        isTouched.doorPlace &&
        sdc.doorPlace !== null &&
        sdc.doorPlace !== "" &&
        sdc.doorPlace.length > CHARACTER_LIMIT
          ? `設置場所は${CHARACTER_LIMIT}文字以内で入力してください。`
          : "",
      memo:
        isTouched.memo && sdc.memo !== "" && sdc.memo.length > CHARACTER_LIMIT
          ? `メモは${CHARACTER_LIMIT}文字以内で入力してください。`
          : "",
      isLteEnabled:
        sdc.isLteEnabled && !sdc.lteApn ? "APNを入力してください" : "",
      isWifiEnabled:
        sdc.isWifiEnabled &&
        (!sdc.ap1Ssid || !sdc.ap1Password) &&
        (!sdc.ap2Ssid || !sdc.ap2Password) &&
        (!sdc.ap3Ssid || !sdc.ap3Password)
          ? "最低一つのAP IP設定を入力してください"
          : "",
      ap1IpConfigType:
        sdc.isWifiEnabled &&
        sdc.ap1IpConfigType === "STATIC" &&
        (!sdc.ap1StaticIp || !sdc.ap1StaticGateWay || !sdc.ap1StaticDns)
          ? "IP、GW、DNSのすべて入力してください"
          : "",
      ap2IpConfigType:
        sdc.isWifiEnabled &&
        sdc.ap2IpConfigType === "STATIC" &&
        (!sdc.ap2StaticIp || !sdc.ap2StaticGateWay || !sdc.ap2StaticDns)
          ? "IP、GW、DNSのすべて入力してください"
          : "",
      ap3IpConfigType:
        sdc.isWifiEnabled &&
        sdc.ap3IpConfigType === "STATIC" &&
        (!sdc.ap3StaticIp || !sdc.ap3StaticGateWay || !sdc.ap3StaticDns)
          ? "IP、GW、DNSのすべて入力してください"
          : "",
      ap1StaticIp:
        sdc.ap1StaticIp && !StaticIpRegex.test(sdc.ap1StaticIp)
          ? StaticIpErrorMessage
          : "",
      ap2StaticIp:
        sdc.ap2StaticIp && !StaticIpRegex.test(sdc.ap2StaticIp)
          ? StaticIpErrorMessage
          : "",
      ap3StaticIp:
        sdc.ap3StaticIp && !StaticIpRegex.test(sdc.ap3StaticIp)
          ? StaticIpErrorMessage
          : "",
      sciSignageIpAddr:
        sdc.sciSignageIpAddr && !IpV4Regex.test(sdc.sciSignageIpAddr)
          ? IpV4ErrorMessage
          : "",
      sdcSerialNumber: "",
    };

    setValidationErrors(errors);
  }, [isTouched, sdc]);

  const handleSelectBuilding = (selectedBuilding: BuildingType) => {
    setSdc((s) => ({
      ...s,
      building: {
        autoBuildingId: selectedBuilding.autoBuildingId,
        buildingManagementId: selectedBuilding.buildingManagementId,
        buildingId: selectedBuilding.buildingId,
        buildingName: selectedBuilding.buildingName,
        buildingAddress: selectedBuilding.buildingAddress,
      },
    }));
    setOpenBuildingModal(false);
  };

  const [openBuildingModal, setOpenBuildingModal] = useState(false);

  const handleFormSubmit = (event: React.FormEvent) => {
    event.preventDefault();
  };

  const fetchDoorData = async () => {
    setIsLoading(true);
    setHasError(false);

    const jwtToken = await getJwtTokenFromIdToken();

    try {
      const response = await fetch(
        process.env.REACT_APP_API_ENDPOINT + "/sdcs/" + autoSdcIdInUrl,
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: jwtToken,
          },
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      setFetchedTs(Date.now());
      const data = (await response.json()) as SdcDetails;
      console.log("SDC（ドア）詳細データ" + JSON.stringify(data));
      // APIからの初期値を保持
      setInitialBillingStartAt(data.billingStartAt);
      setSdc(data);
    } catch (error) {
      console.error("Error fetching door data:", error);
      setHasError(true);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchDoorData();
  }, [autoSdcIdInUrl]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []); //ページロード時にスクロール位置をリセット

  if (isLoading) {
    return <LoadingSpinner />;
  }

  if (hasError) {
    return <ErrorWithRetry onRetry={fetchDoorData} />;
  }

  const handleUpdateClick = async () => {
    if (sdc.building && !sdc.doorPlace) {
      setValidationErrors((prevErrors) => ({
        ...prevErrors,
        doorPlace: !sdc.doorPlace ? `設置場所は必須です` : prevErrors.doorPlace,
      }));
      setSnackbarMessage("入力エラーがあります。修正してください。");
      setAlertSeverity("error");
      setSnackbarOpen(true);
      return; // エラーがあるため、中断
    }

    if (!sdc.isWifiEnabled && !sdc.isLteEnabled) {
      setSnackbarMessage("LTEまたはWi-Fiのどちらかを有効にしてください。");
      setAlertSeverity("error");
      setSnackbarOpen(true);
      return; // エラーがあるため、中断
    }

    // エラーがある場合、処理を阻止
    if (Object.values(validationErrors).some((error) => error !== "")) {
      setSnackbarMessage("入力エラーがあります。修正してください。");
      setAlertSeverity("error");
      setSnackbarOpen(true);
      return;
    }

    // JWTトークンを取得します
    const jwtToken = await getJwtTokenFromIdToken();

    const requestData = {
      lastTsFetchedAt: fetchedTs,
      sdc: {
        shippingDate: sdc.shippingDate,
        isLteEnabled: sdc.isLteEnabled,
        lteApn: sdc.lteApn,
        lteId: sdc.lteId,
        ltePassword: sdc.ltePassword,
        lteAuthType: sdc.lteAuthType,
        isWifiEnabled: sdc.isWifiEnabled,
        defaultWifi: sdc.defaultWifi,
        ap1Ssid: sdc.ap1Ssid,
        ap1Password: sdc.ap1Password,
        ap1IpConfigType: sdc.ap1IpConfigType,
        ap1StaticIp: sdc.ap1StaticIp,
        ap1StaticGateWay: sdc.ap1StaticGateWay,
        ap1StaticDns: sdc.ap1StaticDns,
        ap2Ssid: sdc.ap2Ssid,
        ap2Password: sdc.ap2Password,
        ap2IpConfigType: sdc.ap2IpConfigType,
        ap2StaticIp: sdc.ap2StaticIp,
        ap2StaticGateWay: sdc.ap2StaticGateWay,
        ap2StaticDns: sdc.ap2StaticDns,
        ap3Ssid: sdc.ap3Ssid,
        ap3Password: sdc.ap3Password,
        ap3IpConfigType: sdc.ap3IpConfigType,
        ap3StaticIp: sdc.ap3StaticIp,
        ap3StaticGateWay: sdc.ap3StaticGateWay,
        ap3StaticDns: sdc.ap3StaticDns,
        isAalEnabled: sdc.isAalEnabled,
        isBleEnabled: sdc.isBleEnabled,
        isNfcEnabled: sdc.isNfcEnabled,
        isNfcCertRequired: sdc.isNfcCertRequired,
        isSciEnabled: sdc.isSciEnabled,
        isGuideBellEnabled: sdc.isGuideBellEnabled,
        isSpeakerEnabled: sdc.isSpeakerEnabled,
        isSignageEnabled: sdc.isSignageEnabled,
        doorPlace: sdc.doorPlace,
        installationDate: sdc.installationDate,
        // initialBillingStartAtが存在する場合は課金開始日を送信しない　これやらないとサーバー側で上書き禁止のエラー出される
        ...(initialBillingStartAt === null ||
        initialBillingStartAt === undefined ||
        initialBillingStartAt === ""
          ? {
              // 課金開始日時が存在する場合は00:00:00を付与、ない場合はnull
              billingStartAt: sdc.billingStartAt
                ? new Date(`${sdc.billingStartAt}T00:00:00+09:00`).toISOString()
                : null,
            }
          : {}),
        memo: sdc.memo,
        sciSignageIpAddr: sdc.sciSignageIpAddr,
        idInCluster: sdc.idInCluster,
        isDefaultOpenHalf: sdc.isDefaultOpenHalf,
        isPushButtonEnabled: sdc.isPushButtonEnabled,
        defaultDistanceNear: sdc.defaultDistanceNear,
        defaultDistanceFar: sdc.defaultDistanceFar,
        txPowerLevel: sdc.txPowerLevel,
        isSpeakerContactEnabled: sdc.isSpeakerContactEnabled,
        logMode: sdc.logMode,
        autoBuildingId:
          sdc.building === null ? null : sdc.building.autoBuildingId,
      },
    };
    setIsUpdating(true); // 更新処理開始
    try {
      // call updateSdc API
      const response = await fetch(
        process.env.REACT_APP_API_ENDPOINT +
          `/sdcs/${autoSdcIdInUrl}?version=2`,
        {
          method: "PATCH",
          headers: {
            "Content-Type": "application/json",
            Authorization: jwtToken,
          },
          body: JSON.stringify(requestData),
        }
      );

      if (response.status === 409) {
        setSnackbarMessage(
          "編集中にSDC情報に更新がありました。画面の再読込をしてください。"
        );
        setAlertSeverity("error");
        setSnackbarOpen(true);
      } else if (response.ok) {
        // 更新成功の場合の処理
        console.log("SDC（ドア）の更新が成功しました。");
        setSnackbarMessage("SDC（ドア）の更新が成功しました。");
        setAlertSeverity("success");
        setSnackbarOpen(true);
        setFormEdited(false);

        // 更新に成功したらfetchタイムスタンプを更新
        setFetchedTs(Date.now());
      } else {
        // 更新失敗の場合の処理
        console.error("SDC（ドア）の更新に失敗しました。");
        setSnackbarMessage("SDC（ドア）の更新に失敗しました。");
        setAlertSeverity("error");
        setSnackbarOpen(true);
      }
    } catch (error) {
      console.error("SDC（ドア）の更新時にエラーが発生しました:", error);
      setSnackbarMessage("SDC（ドア）の更新時にエラーが発生しました");
      setAlertSeverity("error");
      setSnackbarOpen(true);
    } finally {
      setIsUpdating(false); // 更新処理終了
    }
  };

  const handleProceedRestartSdc = async () => {
    // JWTトークンを取得します
    const jwtToken = await getJwtTokenFromIdToken();

    try {
      // call restartSdc API
      const response = await fetch(
        process.env.REACT_APP_API_ENDPOINT + `/sdc/${sdc.sdcUuid}/restart`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: jwtToken,
          },
          body: "{}",
        }
      );

      if (response.ok) {
        // API呼び出し成功の場合の処理
        console.log("SDCの再起動を開始しました。");
        setSnackbarMessage("SDCの再起動を開始しました。");
        setAlertSeverity("success");
        setSnackbarOpen(true);
      } else {
        // API呼び出し失敗の場合の処理
        console.error("SDCの再起動リクエストに失敗しました。");
        setSnackbarMessage("SDCの再起動リクエストに失敗しました。");
        setAlertSeverity("error");
        setSnackbarOpen(true);
      }
    } catch (error) {
      console.error("SDCの再起動リクエスト時にエラーが発生しました:", error);
      setSnackbarMessage("SDCの再起動リクエスト時にエラーが発生しました");
      setAlertSeverity("error");
      setSnackbarOpen(true);
    } finally {
      setIsRestartOngoing(false);
    }
  };

  const handleCancelClick = () => {
    // フォームが編集されているかどうかを確認する
    if (formEdited) {
      // フォームが編集されている場合は、移動する前に確認
      setOpenConfirm(true);
    } else {
      // フォームが編集されていない場合は、確認せずに移動
      navigate(-1);
    }
  };

  const handleSuccess = (message: string) => {
    setSnackbarMessage(message);
    setAlertSeverity("success");
    setSnackbarOpen(true);
  };

  const handleError = (message: string) => {
    setSnackbarMessage(message);
    setAlertSeverity("error");
    setSnackbarOpen(true);
  };

  return (
    <CustomContainer maxWidth="lg">
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
          >
            <PageTitle title={"SDC（ドア）詳細・編集"} />
          </Box>
        </Grid>
        <Grid item xs={12}>
          <form onSubmit={handleFormSubmit}>
            <DoorDetailsTable
              isRegistration={false}
              sdc={sdc}
              setSdc={setSdc}
              setFormEdited={setFormEdited}
              openBuildingModal={openBuildingModal}
              setOpenBuildingModal={setOpenBuildingModal}
              handleSelectBuilding={handleSelectBuilding}
              handleBlur={handleBlur}
              validationErrors={validationErrors}
              onSuccess={handleSuccess}
              onError={handleError}
              refreshSdcData={fetchDoorData}
              setFetchedTs={setFetchedTs}
              initialBillingStartAt={initialBillingStartAt}
              openConfirmDialog={openConfirmDialog} // 追加
              setOpenConfirmDialog={setOpenConfirmDialog} // 追加
            />
            <Grid
              item
              xs={12}
              marginTop={2}
              display="flex"
              justifyContent="flex-end"
              marginBottom={2}
            >
              <Button
                variant="contained"
                color="error"
                style={{ textTransform: "none" }}
                sx={{
                  width: theme.spacing(8),
                }}
                onClick={() => {
                  setOpenConfirmRestart(true);
                }}
                disabled={sdc.sdcUuid === ""}
              >
                {isRestartOngoing ? (
                  <CircularProgress sx={{ color: "white" }} size={"1.5em"} />
                ) : (
                  "SDCを再起動"
                )}
              </Button>
              <Button
                variant="outlined"
                color="inherit"
                onClick={handleCancelClick}
                sx={{ marginLeft: 1 }}
              >
                戻る
              </Button>
              <Button
                type="submit"
                variant="outlined"
                color="primary"
                onClick={handleUpdateClick}
                disabled={isUpdating || sdc.autoSdcId === 0} // 更新処理中は無効化
                sx={{ marginLeft: 1 }}
              >
                {isUpdating ? <CircularProgress size={24} /> : "更新"}
              </Button>
            </Grid>
          </form>
        </Grid>
      </Grid>

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={alertSeverity}
          variant="filled"
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>

      <ConfirmDialog
        open={openConfirm}
        onClose={() => {
          setOpenConfirm(false);
        }}
        onConfirm={() => {
          navigate(`/doors`);
        }}
        title="確認"
        description="編集した内容が破棄されますが良いですか?"
      />
      <ConfirmDialog
        open={openConfirmRestart}
        onClose={() => {
          setOpenConfirmRestart(false);
        }}
        onConfirm={() => {
          handleProceedRestartSdc();
          setIsRestartOngoing(true);
        }}
        title="確認"
        description="今すぐSDCが再起動されますがよろしいですか？"
      />
      <ConfirmDialog
        open={openConfirmDialog}
        onClose={() => setOpenConfirmDialog(false)}
        onConfirm={() => {
          setFormEdited(true);
          setSdc((s) => ({
            ...s,
            isAalEnabled: false, // OFFに設定
          }));
        }}
        title="重要"
        description="FTスマートキー機能をoffにすると、このSDCに登録されている鍵がすべて一時的に無効になります。再度この機能をonにするまではスマホアプリにダウンロードした鍵が使えなくなります。本当にOffにしますか？"
      />
    </CustomContainer>
  );
};

export default DoorEditPage;
